import { useEffect, useState } from 'react';
import { joinWindowStatus, type JoinWindowStatus } from '@proctoring/api-client';
import type { Interview } from '@proctoring/shared';

/**
 * Re-evaluates the join window once a second so the countdown ticks and the button
 * un-disables itself the moment T−15min passes, with no reload and no refetch.
 *
 * The interval is torn down once the window is no longer `waiting`: there is nothing left
 * to count down to, and an open lobby stays open (design §5 — no hard close).
 */
export function useJoinWindow(
  interview: Pick<Interview, 'scheduledAt' | 'state'> | null | undefined,
  intervalMs = 1000,
): JoinWindowStatus | null {
  const [status, setStatus] = useState<JoinWindowStatus | null>(() =>
    interview ? joinWindowStatus(interview, new Date()) : null,
  );

  const scheduledAt = interview?.scheduledAt;
  const state = interview?.state;

  useEffect(() => {
    if (!scheduledAt || !state) {
      setStatus(null);
      return;
    }

    const evaluate = () => joinWindowStatus({ scheduledAt, state }, new Date());
    const initial = evaluate();
    setStatus(initial);
    if (initial.state !== 'waiting') return;

    const timer = setInterval(() => {
      const next = evaluate();
      setStatus(next);
      if (next.state !== 'waiting') clearInterval(timer);
    }, intervalMs);

    return () => clearInterval(timer);
  }, [scheduledAt, state, intervalMs]);

  return status;
}
